import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Tooltip } from '@mui/material';
import { DataGridPremium, GridRow, ToolbarButton, useGridApiRef } from '@mui/x-data-grid-premium';
import { isEmpty, isEqual } from 'lodash/lang';

import {
  useEuiFiltersDispatch,
  useEuiFiltersState,
  withEuiFiltersProvider
} from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import { FRS_READY } from 'components/eui/EuiFiltersContext/reducer';
import { CustomViews } from 'components/pages/patient-source/PmWorkbook/PMWorkbookTable/TableComponents/CustomViews/CustomViews';
import {
  INITIAL_FILTERS_STATE,
  INITIAL_GRID_STATE,
  TASK_WORK_LIST_TABLE_KEY
} from 'components/pages/patient-source/TaskWorklist/constants';
import FiltersSection from 'components/pages/patient-source/TaskWorklist/FiltersSection/FiltersSection';
import {
  createSelectedMap,
  getUniqueSitesFromStudySites,
  toRequestFilters,
  toSsuIds
} from 'components/pages/patient-source/TaskWorklist/TaskWorklistService';

import { TaskApi } from '../../../../api';
import { onFileSave } from '../../../../services/handlers';
import { muiDataGridLocaleText } from '../../../../services/i18n/mui';
import { useActiveTask } from '../../../../store/activeTask';
import { DefaultToolbar } from '../../../MUIDataGrid/DefaultToolbar/DefaultToolbar';
import TasksSupervisor from '../../../root/Container/Layout/Tasks/TasksSupervisor/TasksSupervisor';

import useTableData from './useTableData';

import './TaskWorklist.scss';

const TaskWorklist = () => {
  const activeTask = useActiveTask();

  const previousRequestObject = useRef(null);
  const onceLoaded = useRef(false);

  const [alreadyLoaded, setAlreadyLoaded] = useState(false);
  const [savedState, setSavedState] = useState();

  const apiRef = useGridApiRef();

  const { schema, statuses, ssu, assignee, topics } = useEuiFiltersState();
  const dispatch = useEuiFiltersDispatch();

  const { tasks, setTasks, columns, processRowUpdate, tableVersion, tableName } = useTableData();

  const requestObject = useMemo(
    function() {
      if (schema?.status !== FRS_READY || isEmpty(ssu)) {
        return;
      }
      const { studySites, selectedStudyMap, selectedSiteMap } = ssu;
      const ssuIds = toSsuIds(studySites, selectedStudyMap, selectedSiteMap);
      const requestSsuIds = isEmpty(ssuIds) ? null : ssuIds;
      const nextRequestObject = toRequestFilters(statuses, topics, assignee, requestSsuIds);

      if (isEqual(previousRequestObject.current, nextRequestObject)) {
        return previousRequestObject.current;
      }

      previousRequestObject.current = nextRequestObject;
      return nextRequestObject;
    },
    [schema, statuses, ssu, assignee, topics]
  );

  const loadTableData = useCallback(
    function(request) {
      TaskApi.getAllTasksByFilter(request).then(({ data }) => {
        setTasks(data);
        setAlreadyLoaded(true);
      });
    },
    [setTasks]
  );

  const exportTasks = useCallback(
    function() {
      if (requestObject) {
        TaskApi.exportTasks(requestObject).then(onFileSave);
      }
    },
    [requestObject]
  );

  const onApply = useCallback(
    function() {
      if (requestObject) {
        loadTableData(requestObject);
      }
    },
    [loadTableData, requestObject]
  );

  const onReset = useCallback(
    function() {
      dispatch({
        type: 'PERFORM',
        processor: resetFiltersProcessor
      });
    },
    [dispatch]
  );

  useEffect(() => {
    setAlreadyLoaded(false);
  }, [requestObject]);

  useEffect(() => {
    if (schema?.status !== FRS_READY || !onceLoaded.current) return;
    loadTableData(previousRequestObject.current);
  }, [schema?.status, activeTask.updateTimestamp, loadTableData]);

  useEffect(
    function() {
      if (onceLoaded.current || !requestObject) {
        return;
      }
      loadTableData(requestObject);
      onceLoaded.current = true;
    },
    [loadTableData, requestObject]
  );

  const defaultTableState = useMemo(() => {
    return {
      columns: {
        columnVisibilityModel: Object.fromEntries(columns.map(column => [column.field, true])),
        orderedFields: columns.map(col => col.field)
      },
      density: 'standard',
      filter: {
        quickFilterExcludeHiddenColumns: false
      },
      sorting: {
        sortModel: [
          {
            field: 'dueDate',
            sort: 'asc'
          }
        ]
      },
      sidebar: {
        open: false
      },
      pinnedColumns: {
        left: [],
        right: []
      }
    };
  }, [columns]);

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(`${TASK_WORK_LIST_TABLE_KEY}_CONFIGURATION`, JSON.stringify({ ...currentState }));
    }
  }, [apiRef]);

  useLayoutEffect(() => {
    const savedStateVersion = localStorage?.getItem(`${TASK_WORK_LIST_TABLE_KEY}_VERSION`);
    if (savedStateVersion !== tableVersion) {
      localStorage.removeItem(`${TASK_WORK_LIST_TABLE_KEY}_CONFIGURATION`);
      localStorage.setItem(`${TASK_WORK_LIST_TABLE_KEY}_VERSION`, tableVersion);
    }
  }, [tableVersion]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem(`${TASK_WORK_LIST_TABLE_KEY}_CONFIGURATION`);
    const parsedState = stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {};
    setSavedState(parsedState);
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  if (!savedState) {
    return null;
  }

  return (
    <div className="task-worklist">
      <FiltersSection
        onApply={onApply}
        applyDisabled={alreadyLoaded}
        onReset={onReset}
        gridData={tasks}
        exportTasks={exportTasks}
      />
      <DataGridPremium
        className="task-worklist-table"
        apiRef={apiRef}
        getRowId={row => row.id}
        rows={tasks}
        columns={columns}
        rowHeight={38}
        localeText={muiDataGridLocaleText({ noRowsLabel: 'No Record Found' })}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        onStateChange={saveSnapshot}
        initialState={{
          ...INITIAL_GRID_STATE,
          ...savedState
        }}
        processRowUpdate={processRowUpdate}
        getRowClassName={({ row }) => (row.id === activeTask.taskId ? 'highlight' : '')}
        slots={{
          toolbar: TaskWorklistToolbar,
          row: TaskWorklistRow
        }}
        showToolbar
        onCellClick={(params, event) => {
          if (params.field === 'status') {
            event.stopPropagation();
            if (apiRef.current.getCellMode(params.id, params.field) === 'view') {
              apiRef.current.startCellEditMode({ id: params.id, field: params.field });
            }
          }
        }}
        onRowClick={({ row }) => {
          TasksSupervisor.open(row.id, row.studySiteId);
        }}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true },
            excelOptions: { disableToolbarButton: true },
            csvOptions: { escapeFormulas: false, utf8WithBom: true },
            defaultTableState,
            tableStateFromLocalStorage: savedState,
            columns,
            tableVersion,
            tableName,
            refreshData: onApply
          }
        }}
        sx={{ border: 'none', padding: '0 40px' }}
      />
    </div>
  );
};

function TaskWorklistToolbar({
  defaultTableState,
  tableStateFromLocalStorage,
  columns,
  tableVersion,
  tableName,
  refreshData,
  ...restProps
}) {
  const [quickFilterExpanded, setQuickFilterExpanded] = useState(
    tableStateFromLocalStorage?.filter?.filterModel?.quickFilterValues?.length > 0
  );

  return (
    <DefaultToolbar
      {...restProps}
      quickFilterProps={{ expanded: quickFilterExpanded, onExpandedChange: setQuickFilterExpanded }}
    >
      <Tooltip title="Refresh">
        <ToolbarButton onClick={refreshData} data-testid="refresh-table-button">
          <RefreshIcon fontSize="small" />
        </ToolbarButton>
      </Tooltip>
      <CustomViews
        defaultTableState={defaultTableState}
        tableStateFromLocalStorage={tableStateFromLocalStorage}
        columns={columns}
        tableVersion={tableVersion}
        tableName={tableName}
        setQuickFilterExpanded={setQuickFilterExpanded}
      />
    </DefaultToolbar>
  );
}

const TaskWorklistRow = props => <GridRow {...props} />;

function resetFiltersProcessor(state) {
  const sites = getUniqueSitesFromStudySites(state.ssu.studySites);
  return {
    ...state,
    statuses: INITIAL_FILTERS_STATE.statuses,
    topics: {
      ...state.topics,
      value: state.topics.source
    },
    assignee: {
      ...state.assignee,
      value: state.assignee.source
    },
    ssu: {
      ...state.ssu,
      selectedStudies: state.ssu.studies,
      selectedStudyMap: createSelectedMap(state.ssu.studies),
      sites: sites,
      selectedSites: sites,
      selectedSiteMap: createSelectedMap(sites)
    }
  };
}

export default withEuiFiltersProvider(TaskWorklist, { cachePrefix: 'TWF' });
